import React, { useEffect } from 'react';
import { stripHtml } from '../utils/styleHelpers';
import { LANGUAGE_MAP } from '../i18n/LanguageState';

/**
 * SeoHead — Dynamic Client-side Meta, OpenGraph & Favicon/Apple-Touch-Icon Manager
 * Normalizes page metadata across all routes and injects clean absolute URLs for
 * document title, meta description, OpenGraph (og:*), Twitter Cards, favicons,
 * Apple Touch Icons (for iOS link previews), and JSON-LD Structured Data.
 */
const SeoHead = ({
    title,
    pageTitle: propPageTitle,
    description,
    pageDescription,
    image,
    imageUrl,
    ogImage,
    url,
    type = 'website',
    productData = null,
    siteSettings = {},
    seoSettings = {},
    data = null,
    config = null,
    activeLanguage = null,
    enabledLanguages = null,
}) => {
    const fullData = data || config || {};
    const finalSiteSettings = { ...(fullData.settings || {}), ...siteSettings };
    const finalSeoSettings = { ...(fullData.seo || {}), ...seoSettings };

    const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://omnivaso.com';
    const currentUrl = url || (typeof window !== 'undefined' ? window.location.href : siteUrl);

    const defaultSiteTitle = stripHtml(finalSiteSettings.siteTitle || 'OMNIVASO');
    const rawTitle = title || propPageTitle || finalSeoSettings.metaTitle;
    const rawDesc = description || pageDescription || finalSeoSettings.metaDescription || finalSiteSettings.siteDescription;
    const rawImage = image || imageUrl || ogImage || finalSeoSettings.ogImage || finalSeoSettings.twitterImage || finalSiteSettings.siteLogo || finalSiteSettings.heroBgImage;

    const cleanTitle = rawTitle ? stripHtml(rawTitle) : '';
    const cleanDesc = rawDesc ? stripHtml(rawDesc) : '';

    const computedPageTitle = cleanTitle 
        ? (cleanTitle.includes(defaultSiteTitle) ? cleanTitle : `${cleanTitle} | ${defaultSiteTitle}`) 
        : (finalSeoSettings.metaTitle || defaultSiteTitle);
    
    const metaDescription = (cleanDesc || 'Discover handcrafted products, art collections, and minimal design at Omnivaso.').substring(0, 160);
    
    // Helper to guarantee absolute URLs for social sharing and iOS link preview scrapers
    const makeAbsolute = (urlStr) => {
        if (!urlStr || typeof urlStr !== 'string') return '';
        const trimmed = urlStr.trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) return trimmed;
        const cleanPath = trimmed.replace(/^\.\//, '').replace(/^\//, '');
        return `${siteUrl}/${cleanPath}`;
    };

    const activeFavicon = (finalSeoSettings.favicon || finalSiteSettings.favicon || '/favicon.ico').trim();
    const activeAppleIcon = (finalSeoSettings.appleTouchIcon || finalSiteSettings.appleTouchIcon || '/apple-touch-icon.png').trim();

    const absFavicon = makeAbsolute(activeFavicon);
    const absAppleIcon = makeAbsolute(activeAppleIcon);

    // og:image fallback chain: page image -> ogImage -> twitterImage -> site logo -> apple touch icon -> favicon
    let metaImage = makeAbsolute(rawImage) || absAppleIcon || absFavicon;

    // Resolve og:locale from active language
    const langCode = activeLanguage || 'en';
    const langInfo = LANGUAGE_MAP[langCode] || LANGUAGE_MAP.en;
    const ogLocale = langInfo.locale;

    useEffect(() => {
        // 1. Update Document Title
        document.title = computedPageTitle;

        // Helper to update or create meta tags
        const setMetaTag = (selector, attrName, attrValue, content) => {
            if (!content) return;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attrName, attrValue);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        // Helper to update or create link tags
        const setLinkTag = (rel, href, extraAttrs = {}) => {
            if (!href) return;
            const hreflang = extraAttrs.hreflang;
            let el;
            if (hreflang) {
                el = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
            } else {
                el = document.querySelector(`link[rel="${rel}"]`);
            }
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        // Helper to cleanly update browser Favicon without leaving conflicting default SVG/PNG/ICO tags
        const updateFaviconTags = (favUrl) => {
            if (!favUrl) return;
            document.querySelectorAll('link[rel*="icon"]').forEach(el => {
                const rel = (el.getAttribute('rel') || '').toLowerCase();
                if (rel !== 'apple-touch-icon' && rel !== 'apple-touch-icon-precomposed') {
                    el.remove();
                }
            });

            const iconTag = document.createElement('link');
            iconTag.setAttribute('rel', 'icon');
            if (favUrl.endsWith('.svg')) {
                iconTag.setAttribute('type', 'image/svg+xml');
            } else if (favUrl.endsWith('.png')) {
                iconTag.setAttribute('type', 'image/png');
            } else {
                iconTag.setAttribute('type', 'image/x-icon');
            }
            iconTag.setAttribute('href', favUrl);
            document.head.appendChild(iconTag);
        };

        // Helper to update Apple Touch Icon links (crucial for iOS bookmarks & iOS share previews)
        const updateAppleTouchIconTags = (appleUrl) => {
            if (!appleUrl) return;
            document.querySelectorAll('link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]').forEach(el => el.remove());

            const appleTag = document.createElement('link');
            appleTag.setAttribute('rel', 'apple-touch-icon');
            appleTag.setAttribute('sizes', '180x180');
            appleTag.setAttribute('href', appleUrl);
            document.head.appendChild(appleTag);

            const precomposedTag = document.createElement('link');
            precomposedTag.setAttribute('rel', 'apple-touch-icon-precomposed');
            precomposedTag.setAttribute('href', appleUrl);
            document.head.appendChild(precomposedTag);
        };

        // 2. Standard Meta Tags
        setMetaTag('meta[name="description"]', 'name', 'description', metaDescription);
        setLinkTag('canonical', currentUrl);

        // 3. Favicon & Apple Touch Icons
        updateFaviconTags(absFavicon);
        updateAppleTouchIconTags(absAppleIcon);

        // 4. OpenGraph Meta Tags (Facebook / WhatsApp / iMessage / iOS link preview)
        setMetaTag('meta[property="og:title"]', 'property', 'og:title', computedPageTitle);
        setMetaTag('meta[property="og:description"]', 'property', 'og:description', metaDescription);
        setMetaTag('meta[property="og:type"]', 'property', 'og:type', type);
        setMetaTag('meta[property="og:url"]', 'property', 'og:url', currentUrl);
        setMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', defaultSiteTitle);
        setMetaTag('meta[property="og:locale"]', 'property', 'og:locale', ogLocale);
        if (metaImage) {
            setMetaTag('meta[property="og:image"]', 'property', 'og:image', metaImage);
        }

        // 5. Twitter Card Meta Tags
        setMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', metaImage ? 'summary_large_image' : 'summary');
        setMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', computedPageTitle);
        setMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', metaDescription);
        if (metaImage) {
            setMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', metaImage);
        }

        // 6. hreflang alternate links (i18n SEO)
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
        if (enabledLanguages && enabledLanguages.length > 1) {
            const baseUrl = currentUrl.split('?')[0].replace(/[?&]lang=[a-z-]+/i, '');
            enabledLanguages.forEach(code => {
                const info = LANGUAGE_MAP[code];
                if (!info) return;
                setLinkTag('alternate', baseUrl, { hreflang: info.hreflang });
            });
            setLinkTag('alternate', baseUrl, { hreflang: 'x-default' });
        }

        // 7. JSON-LD Structured Data Schema Injection
        let schemaScript = document.getElementById('json-ld-schema');
        if (!schemaScript) {
            schemaScript = document.createElement('script');
            schemaScript.id = 'json-ld-schema';
            schemaScript.type = 'application/ld+json';
            document.head.appendChild(schemaScript);
        }

        let jsonLdData = null;
        if (type === 'product' && productData) {
            jsonLdData = {
                "@context": "https://schema.org/",
                "@type": "Product",
                "name": productData.name || computedPageTitle,
                "image": metaImage ? [metaImage] : [],
                "description": metaDescription,
                "sku": productData.id || '',
                "offers": {
                    "@type": "Offer",
                    "url": currentUrl,
                    "priceCurrency": productData.currency || "USD",
                    "price": Number(productData.price || 0).toFixed(2),
                    "availability": productData.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
                }
            };
        } else {
            jsonLdData = {
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": defaultSiteTitle,
                "url": siteUrl,
                "description": metaDescription,
                "inLanguage": langInfo.hreflang,
            };
        }

        schemaScript.textContent = JSON.stringify(jsonLdData);
    }, [computedPageTitle, metaDescription, metaImage, absFavicon, absAppleIcon, currentUrl, type, ogLocale, JSON.stringify(productData || {}), JSON.stringify(enabledLanguages || [])]);

    return null;
};

export default SeoHead;
